<template>
    <div class="company-vertical-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Company Vertical</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div v-if="!isProcessing" class="content-wrapper">
                <div class="add-vertical-buttons-wrapper">
                    <app-button v-if="!isAddVerticalShown" @click="showAddVerticalSection">
                        Add New
                    </app-button>
                    <app-button v-else @click="hideAddVerticalSection">
                        Cancel
                    </app-button>
                </div>
                <div class="add-vertical-wrapper" v-if="isAddVerticalShown">
                    <h3 class="upper-text">Add New Vertical</h3>
                    <div class="flex-wrapper">
                        <app-input
                            v-model="vertical.name" 
                            :label="'Name'" />
                        <app-button @click="saveNewVertical" :loading="isSavingNewVertical" :disabled="isSavingNewVertical">
                            Save Vertical
                        </app-button>
                    </div>
                </div>
                <div class="vertical-wrapper">
                    <div v-for="vertical in verticals" :class="['vertical', { 'editing': vertical.isEditShown }]" :key="vertical.id">
                        <div class="vertical-wrapper">
                            <span v-if="!vertical.isEditShown" class="vertical-name">{{ vertical.name.value }}</span>
                            <app-input v-else
                                v-model="vertical.name.edit" 
                                :label="'Name'" />
                        </div>
                        <div v-if="vertical.isEditShown" class="vertical-edit-buttons-wrapper">
                            <app-button
                                @click="updateVertical(vertical)"
                                size="small"
                                :disabled="vertical.isUpdating"
                                :loading="vertical.isUpdating">
                                Save
                            </app-button>
                            <app-button 
                                @click="cancelVerticalEdit(vertical)"
                                size="small"
                                class="red-btn"
                                :disabled="vertical.isUpdating"
                                :loading="vertical.isUpdating">
                                Cancel
                            </app-button>
                        </div>
                        <div v-else class="vertical-edit-buttons-wrapper">
                            <app-button
                                @click="showVerticalEdit(vertical)"
                                size="small"
                                :disabled="vertical.isDeleting">
                                Edit
                            </app-button>
                            <app-button
                                @click="deleteVertical(vertical)"
                                class="red-btn"
                                size="small"
                                :disabled="vertical.isDeleting"
                                :loading="vertical.isDeleting">
                                Delete
                            </app-button>
                        </div>
                    </div>
                </div>
            </div>
            <loading-screen-svg v-else></loading-screen-svg>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="transparent-btn" @click="closeWidget">
                        Cancel
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const AppInput = () => import('@/components/common/app-input');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const LoadingScreenSvg = ()  => import('@/components/svg/loading-screen-svg');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'company-vertical-widget',

    props: {
        options: {
            type: Object
        },

        widget: {
            type: Object,
            required: true
        }
    },

    components: {
        'app-button': AppButton,
        'app-input': AppInput,
        'widget-close-svg': WidgetCloseSvg,
        'loading-screen-svg': LoadingScreenSvg
    },

    data() {
        return {
            isProcessing: true,
            verticals: [],
            isAddVerticalShown: false,
            isColorPickerShown: false,
            isSavingNewVertical: false,
            vertical: {
                id: null,
                name: ''
            },

            clientId: null
        }
    },

    methods: {
        getVerticals() {
            this.isProcessing = true;
            this.$api
                .get(`/service/Verticals/?$filter=ClientId eq ${ this.clientId }`)
                .then(response => {
                    var verticalResponse = response.data.value;
                    this.setVerticals(verticalResponse);
                    this.isProcessing = false;
                })
                .catch(error => {
                    this.$helpers.showFailNotification('An error occurred while getting your company verticals.', { error });
                    this.isProcessing = false;
            });
        },
        
        setVerticals(response) {
            this.verticals = response.map(vertical => {
                return {
                    id: vertical.Id,
                    clientId: vertical.ClientId,
                    name: {
                        value: vertical.Name,
                        edit: vertical.Name
                    },

                    isEditShown: false,
                    isUpdating: false,
                    isDeleting: false
                };
            });
        },

        // ADD
        showAddVerticalSection() {
            this.isAddVerticalShown = true;
        },

        hideAddVerticalSection() {
            this.isAddVerticalShown = false;
        },

        saveNewVertical() {
            this.isSavingNewVertical = true;

            var request = {
                Name: this.vertical.name,
                ClientId: this.clientId
            }

            this.$api
                .post('/api/Verticals/', request)
                .then(response => {
                    this.verticals.unshift({
                        id: response.data.Id,
                        clientId: response.data.ClientId,
                        name: {
                            value: response.data.Name,
                            edit: response.data.Name
                        },

                        isEditShown: false,
                        isUpdating: false,
                        isDeleting: false
                    });

                    this.isSavingNewVertical = false;
                    this.isAddVerticalShown = false;

                    this.clearVertical();
                    EventBus.$emit('new-vertical-created');
                })
                .catch(error => {
                    this.isSavingNewVertical = false;
                    this.$helpers.showFailNotification('An error occurred while creating your vertical.', { error })
                });
        },

        clearVertical() {
            this.vertical.name = '';
        },

        // EDIT
        updateVertical(vertical) {
            vertical.isUpdating = true;

            var changes = [{
                'name': 'Name',
                'value': vertical.name.edit
            }];

            this.$api
                .patch(`/api/Verticals/${ vertical.id }`, changes)
                .then(() => {
                    vertical.isUpdating = false;
                    vertical.isEditShown = false;

                    vertical.name.value = vertical.name.edit;

                    vertical.isEditShown = false;
                    EventBus.$emit('vertical-updated');
                })
                .catch((error) => {
                    vertical.isUpdating = false;
                    vertical.isEditShown = false;
                    this.$helpers.showFailNotification('An error occurred while updating your vertical.', { error })
                });
        },

        showVerticalEdit(vertical) {
            vertical.isEditShown = true;
        },

        cancelVerticalEdit(vertical) {
            vertical.name.edit = vertical.name.value;
            vertical.isEditShown = false;
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        // DELETE

        deleteVertical(vertical) {
            vertical.isDeleting = true;

            this.$api
                .delete(`/api/Verticals/${ vertical.id }`)
                .then(response => {
                    vertical.isDeleting = false;
                    var matchedVertical = this.verticals.find(type => {
                        return type.id === vertical.id;
                    });

                    this.verticals.splice(this.verticals.indexOf(matchedVertical), 1);
                    this.$helpers.showSuccessNotification(`"${ vertical.name.edit }" has been removed.`);
                    EventBus.$emit('vertical-deleted');
                })
                .catch(error => {
                    this.isDeleting = false;
                    vertical.isEditShown = false;
                    this.$helpers.showFailNotification(`An error occurred while deleting "${ vertical.name.edit }".`, { error });
                });
        },
    },

    created() {
        this.clientId = this.widget.options.clientId;
        this.getVerticals();
    }
}
</script>

<style>
.company-vertical-widget .flex-wrapper {
    display: flex;
    width: 100%;
    align-items: flex-end;
    justify-content: space-between; 
}

.company-vertical-widget .flex-wrapper .app-input {
    width: 60%;
}

.company-vertical-widget .content-wrapper .add-vertical-buttons-wrapper .btn {
    margin-bottom: 25px;
}

.company-vertical-widget .content-wrapper .add-vertical-wrapper {
    margin-bottom: 25px;
    display: flex;
    align-items: flex-end;
    padding: 25px 15px 15px 15px;
    border: 2px solid #E9E9EF;
    border-radius: 20px;
    position: relative;
}

.company-vertical-widget .content-wrapper .add-vertical-wrapper .upper-text {
    position: absolute;
    top: -10px;
    left: 15px;
    background: #FFFFFF;
    padding: 0px 15px;
}

.company-vertical-widget .content-wrapper .vertical-wrapper .vertical {
    padding: 5px 10px;
    border-radius: 20px;
    border: 2px solid #E9E9EF;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
}

.company-vertical-widget .content-wrapper .vertical-wrapper .vertical .vertical-wrapper p {
    color: #877D88;
    font-size: 16px;
    margin-bottom: 0;
}

.company-vertical-widget .content-wrapper .vertical-wrapper .vertical .vertical-edit-buttons-wrapper {
    display: flex;
}

.company-vertical-widget .content-wrapper .vertical-wrapper .vertical.editing .vertical-edit-buttons-wrapper {
    flex-direction: column;
}

.company-vertical-widget .content-wrapper .vertical-wrapper .vertical .vertical-edit-buttons-wrapper .btn:nth-child(1) {
    margin-right: 10px;
}

.company-vertical-widget .content-wrapper .vertical-wrapper .vertical.editing .vertical-edit-buttons-wrapper .btn:nth-child(1) {
    margin-right: 0;
    margin-bottom: 10px;
}

.company-vertical-widget .content-wrapper .vertical-wrapper .vertical .vertical-edit-buttons-wrapper .red-btn {
    background: #F30070;
    border: 1px solid #F30070;
    color: #FFFFFF;
}

.company-vertical-widget .content-wrapper .vertical-wrapper .vertical .vertical-edit-buttons-wrapper .red-btn:hover {
    background: #D10E68;
    border: 1px solid #D10E68;
}

.company-vertical-widget .content-wrapper .custom-selector-container {
    min-width: 85px;
    width: 85px;
}

.company-vertical-widget .vertical-name {
    color: #877D88;
}
</style>